import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Typography,
  Tooltip,
  Alert,
} from 'antd';
import {
  ArrowLeftOutlined,
  FileImageOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  WarningOutlined,
  MailOutlined,
  DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import apiService from '../../services/api';

const { Title, Text } = Typography;

const STATUS_CONFIG = {
  success: {
    color: 'success',
    icon: <CheckCircleOutlined />,
    label: 'Sent',
  },
  no_caption: {
    color: 'warning',
    icon: <WarningOutlined />,
    label: 'No Reference',
  },
  error: {
    color: 'error',
    icon: <CloseCircleOutlined />,
    label: 'Error',
  },
};

// ─── Date list view ───────────────────────────────────────────────────────────

const DateListView = ({ onSelectDate }) => {
  const [dateSummaries, setDateSummaries] = useState([]);
  const [loading, setLoading] = useState(false);

  const today = dayjs().format('YYYY-MM-DD');

  useEffect(() => {
    const fetchDates = async () => {
      try {
        setLoading(true);
        const res = await apiService.pods.getDates();
        const data = res.data || [];
        const hasToday = data.some(d => d.date === today);
        const list = hasToday ? data : [{ date: today, count: 0, errorCount: 0 }, ...data];
        setDateSummaries(list);
      } catch {
        // silently fail — empty list is fine
      } finally {
        setLoading(false);
      }
    };
    fetchDates();
  }, [today]);

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (d) => (
        <Space>
          <Text strong>{dayjs(d).format('ddd DD MMM YYYY')}</Text>
          {d === today && <Tag color="blue">Today</Tag>}
        </Space>
      ),
    },
    {
      title: 'PODs',
      dataIndex: 'count',
      key: 'count',
      width: 80,
      align: 'center',
      render: (v) => <Text type={v === 0 ? 'secondary' : undefined}>{v}</Text>,
    },
    {
      title: 'Issues',
      dataIndex: 'errorCount',
      key: 'errorCount',
      width: 80,
      align: 'center',
      render: (v) =>
        v > 0 ? (
          <Tag color="red">{v}</Tag>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Button size="small" onClick={() => onSelectDate(record.date)}>
          {record.count === 0 ? 'View' : 'Open'}
        </Button>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <FileImageOutlined style={{ fontSize: 18, marginRight: 8, color: '#1677ff' }} />
        <Title level={4} style={{ margin: 0 }}>Proof of Delivery</Title>
      </div>
      <Table
        dataSource={dateSummaries}
        columns={columns}
        rowKey="date"
        loading={loading}
        pagination={false}
        size="small"
        onRow={(record) => ({ onClick: () => onSelectDate(record.date), style: { cursor: 'pointer' } })}
        style={{ borderRadius: 8, overflow: 'hidden' }}
        locale={{ emptyText: 'No PODs received yet' }}
      />
    </div>
  );
};

// ─── Day detail view ──────────────────────────────────────────────────────────

const DayDetailView = ({ date, onBack }) => {
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const handleDownload = async (pod) => {
    try {
      setDownloading(pod._id);
      const res = await apiService.pods.getDownloadUrl(pod._id);
      window.open(res.data.url, '_blank');
    } catch {
      // silently fail — button will re-enable
    } finally {
      setDownloading(null);
    }
  };

  const fetchPods = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiService.pods.getAll({ date });
      setPods(res.data || []);
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, [date]);

  useEffect(() => {
    fetchPods();
  }, [fetchPods]);

  const successCount = pods.filter(p => p.status === 'success').length;
  const issueCount = pods.filter(p => p.status !== 'success').length;

  const columns = [
    {
      title: 'Time',
      dataIndex: 'receivedAt',
      key: 'receivedAt',
      width: 80,
      render: (v) => <Text type="secondary">{dayjs(v).format('HH:mm')}</Text>,
    },
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      width: 180,
      render: (v, record) =>
        record.status === 'no_caption' ? (
          <Text type="secondary" italic>No reference</Text>
        ) : (
          <Text strong>{v}</Text>
        ),
    },
    {
      title: 'From',
      dataIndex: 'from',
      key: 'from',
      width: 180,
      render: (v) => <Text type="secondary">{v?.replace('whatsapp:', '') || '—'}</Text>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (v) => {
        const cfg = STATUS_CONFIG[v] || STATUS_CONFIG.error;
        return (
          <Tag icon={cfg.icon} color={cfg.color}>
            {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'emailSent',
      key: 'emailSent',
      width: 80,
      align: 'center',
      render: (sent, record) =>
        record.status === 'success' ? (
          <Tooltip title={`Sent to ${record.emailTo}`}>
            <MailOutlined style={{ color: '#3f8600' }} />
          </Tooltip>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: 'Detail',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (msg, record) => {
        if (record.status === 'no_caption') {
          return (
            <Text type="warning" style={{ fontSize: 12 }}>
              Image received without a reference number caption
            </Text>
          );
        }
        if (record.status === 'error') {
          return (
            <Text type="danger" style={{ fontSize: 12 }}>
              Failed to process — please resend
            </Text>
          );
        }
        return null;
      },
    },
    {
      title: '',
      key: 'download',
      width: 48,
      align: 'center',
      render: (_, record) =>
        record.s3Key ? (
          <Tooltip title="Download image">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              loading={downloading === record._id}
              onClick={() => handleDownload(record)}
            />
          </Tooltip>
        ) : null,
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack} style={{ marginRight: 12 }} />
        <FileImageOutlined style={{ fontSize: 16, marginRight: 8, color: '#1677ff' }} />
        <Title level={4} style={{ margin: 0 }}>
          {dayjs(date).format('ddd DD MMM YYYY')}
        </Title>
        {pods.length > 0 && (
          <Space style={{ marginLeft: 16 }}>
            <Tag color="success">{successCount} sent</Tag>
            {issueCount > 0 && <Tag color="error">{issueCount} issue{issueCount !== 1 ? 's' : ''}</Tag>}
          </Space>
        )}
      </div>

      {issueCount > 0 && (
        <Alert
          type="warning"
          showIcon
          message={`${issueCount} POD${issueCount !== 1 ? 's' : ''} could not be processed`}
          description="Ask the driver to resend with a reference number as the image caption."
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        dataSource={pods}
        columns={columns}
        rowKey="_id"
        loading={loading}
        pagination={false}
        size="small"
        style={{ borderRadius: 8, overflow: 'hidden' }}
        locale={{ emptyText: 'No PODs received on this date' }}
        rowClassName={(record) => record.status !== 'success' ? 'pod-row-issue' : ''}
      />
    </div>
  );
};

// ─── Root component ───────────────────────────────────────────────────────────

const PodList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedDate = searchParams.get('date') || null;

  const handleSelectDate = (date) => setSearchParams({ date });
  const handleBack = () => setSearchParams({});

  if (selectedDate) {
    return <DayDetailView date={selectedDate} onBack={handleBack} />;
  }
  return <DateListView onSelectDate={handleSelectDate} />;
};

export default PodList;
